import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/esg";

const img = (
  file: string,
  alt: string,
  width: number,
  height: number,
  span: 2 | 3 | 6 = 3,
  small = true,
) => ({
  src: `${dir}/${file}.webp`,
  ...(small ? { src900: `${dir}/${file}-900.webp` } : {}),
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

export const esg: CaseStudy = {
  slug: "esg",
  title: "ESG",
  cardTitle: "Комплексное продвижение строительной компании ESG",
  subtitle: "Строительство частных домов и коммерческих объектов",
  industry: { label: "Строительство", slug: "construction" },
  eyebrow: "Комплексный маркетинг · Строительство",
  h1: "Комплексное продвижение строительной компании ESG: фирменный стиль, сайт, SEO и Яндекс Директ",
  lead:
    "Для строительной компании ESG выстроили полную маркетинговую систему: фирменный стиль и архитектуру бренда для двух направлений — частного (ESG Villas) и коммерческого (ESG Professional), сайт с каталогом проектов домов и B2B-разделом, SEO-структуру под услуги и типы объектов, кампании в Яндекс Директе и сквозную аналитику обращений.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить похожий проект" },

  seo: {
    primaryKeyword: "продвижение строительной компании",
    secondaryKeywords: [
      "разработка сайта для строительной компании",
      "создание сайта строительной компании",
      "сайт компании по строительству домов",
      "SEO-продвижение строительной компании",
      "продвижение сайта строительной компании",
      "реклама строительной компании в Яндекс Директе",
      "маркетинг для строительной компании",
      "фирменный стиль строительной компании",
      "сайт по строительству домов под ключ",
      "продвижение компании по строительству коммерческих объектов",
    ],
    semanticEntities: [
      "строительная компания",
      "строительство домов под ключ",
      "загородные дома",
      "монолитное строительство",
      "дома из клееного бруса",
      "дома из камня",
      "отделочные работы",
      "коммерческое строительство",
      "гостиницы",
      "банные комплексы",
      "тендер",
      "каталог проектов домов",
      "фирменный стиль",
      "архитектура бренда",
      "SEO-структура",
      "Яндекс Директ",
      "веб-аналитика",
      "заявки с сайта",
      "адаптивная версия",
    ],
    excludedKeywords: [
      "строительство домов Москва",
      "построить дом под ключ цена",
      "дом из клееного бруса цена",
      "строительная компания Москва",
      "проект дома купить",
      "стоимость строительства дома за м2",
      "подрядчик на строительство",
    ],
    title: "Продвижение строительной компании ESG — кейс HAY.agency",
    description:
      "Кейс HAY.agency: комплексный маркетинг строительной компании ESG — фирменный стиль, сайт с каталогом проектов домов, B2B-направление, SEO-структура, Яндекс Директ и аналитика.",
    h1: "Комплексное продвижение строительной компании ESG: фирменный стиль, сайт, SEO и Яндекс Директ",
    slug: "esg",
    canonicalUrl: `${SITE_URL}/cases/esg`,
    ogTitle: "Комплексное продвижение строительной компании ESG",
    ogDescription:
      "Фирменный стиль, сайт, SEO и Яндекс Директ для двух направлений строительной компании: частного ESG Villas и коммерческого ESG Professional.",
    ogImage: `${SITE_URL}${dir}/esg-og.webp`,
    robotsIndex: true,
    datePublished: "2024-02-01",
    dateModified: "2026-08-29",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Основа проекта: структура, дизайн и разработка сайта строительной компании",
    },
    relatedIndustry: {
      label: "SEO-продвижение сайтов услуг",
      to: routes.seo,
      note: "Поисковая структура под услуги строительства и типы объектов",
    },
    relatedCases: [
      {
        label: "Сайт-портфолио для архитектурного бюро Archidica",
        to: "/cases/archidica",
        note: "Проект с большим визуальным портфолио и отраслевыми страницами",
      },
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "Каталог продукции для загородного строительства",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен комплексный маркетинг для строительной компании?",
    text:
      "Соберём фирменный стиль, сайт, SEO-структуру и рекламу в одну систему — с разделением частного и коммерческого направлений.",
    button: "Обсудить продвижение",
  },

  client: {
    name: "ESG",
    legalName: "ЕвроСтройГруп",
    url: "https://esg-moscow.ru/",
    city: "Москва и Московская область",
    industry: "Строительство частных домов и коммерческих объектов",
  },
  year: "Уточняется",
  services: [
    "Фирменный стиль и архитектура бренда",
    "Аналитика и проектирование",
    "UX/UI-дизайн",
    "Разработка сайта",
    "SEO-архитектура и продвижение",
    "Яндекс Директ",
    "Веб-аналитика",
    "Поддержка и развитие проекта",
  ],
  platform: "Tilda с кастомным кодом",
  tags: [
    { label: "Корпоративные сайты", to: routes.corporate },
    { label: "SEO-продвижение", to: routes.seo },
    { label: "Контекстная реклама", to: routes.context },
  ],

  facts: [
    { label: "Клиент", value: "ESG (ЕвроСтройГруп)" },
    { label: "Отрасль", value: "Строительство домов и коммерческих объектов" },
    { label: "Тип проекта", value: "Комплексный маркетинг: бренд, сайт, SEO, реклама" },
    { label: "География", value: "Москва и Московская область" },
    { label: "Направления", value: "ESG Villas — частное, ESG Professional — коммерческое" },
    { label: "Платформа", value: "Tilda с кастомным кодом" },
    {
      label: "Услуги",
      value:
        "Фирменный стиль, проектирование, дизайн, разработка, SEO, Яндекс Директ, аналитика",
    },
    {
      label: "Аудитория",
      value: "Частные заказчики загородных домов, застройщики, отельеры и тендерные комитеты",
    },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Единую архитектуру бренда с двумя логотипами и разделением аудиторий: ESG Villas и ESG Professional.",
      "Сайт с каталогом проектов домов, страницами объектов, планировками и расчётом стоимости.",
      "Отдельное B2B-направление с описанием компетенций, реализованных объектов и формой участия в тендере.",
      "SEO-структуру: посадочные страницы под виды работ, материалы и типы объектов.",
      "Кампании в Яндекс Директе под частный и коммерческий спрос с раздельными сценариями обращения.",
      "Сквозной сбор обращений с форм, расчёта стоимости и телефонных заявок.",
    ],
  },

  about: {
    title: "Одна компания — два разных рынка",
    paragraphs: [
      "ESG (ЕвроСтройГруп) строит загородные дома под ключ по авторским проектам и одновременно работает на рынке коммерческого строительства: гостиницы, банные комплексы, общественные и производственные объекты.",
      "Эти два направления живут по разным правилам. Частный заказчик выбирает эмоционально: смотрит визуализации, планировки, материалы и стоимость. Коммерческий заказчик оценивает компетенции, опыт, реализованные объекты и готовность компании участвовать в тендере.",
      "До проекта оба направления смешивались в одном рекламном сообщении: частные клиенты не находили нужных проектов, а бизнес не видел подтверждения квалификации подрядчика.",
    ],
    images: [
      img("esg-construction-website-homepage", "Главная страница сайта строительной компании ESG", 1600, 1000),
      img("esg-house-projects-catalog", "Каталог проектов загородных домов ESG Villas", 1600, 1000),
      img(
        "esg-commercial-construction-website",
        "Раздел коммерческого строительства ESG Professional",
        1600,
        1000,
      ),
      img("esg-professional-works", "Реализованные коммерческие объекты ESG Professional", 1600, 1000),
    ],
  },

  challenge: {
    title: "Разделить аудитории, не разделив компанию",
    paragraphs: [
      "Задача состояла в том, чтобы выстроить маркетинговую систему для двух рынков с общей репутацией и общим производственным ресурсом.",
    ],
    steps: [
      {
        title: "Построить архитектуру бренда",
        text: "Разделить частное и коммерческое направления визуально и по смыслу, сохранив единый материнский бренд ESG.",
      },
      {
        title: "Сделать выбор дома понятным",
        text: "Показать проекты, планировки, материалы и ориентировочную стоимость так, чтобы заказчик мог сравнивать варианты.",
      },
      {
        title: "Доказать компетенции для B2B",
        text: "Собрать реализованные объекты, виды работ и опыт компании в отдельный блок для тендерных решений.",
      },
      {
        title: "Собрать спрос из поиска и рекламы",
        text: "Разделить SEO-структуру и рекламные кампании по услугам, материалам и типам объектов.",
      },
    ],
  },

  columnBlocks: [
    {
      id: "brand",
      title: "Архитектура бренда: ESG Villas и ESG Professional",
      paragraphs: [
        "Основой стал единый знак ESG, от которого построили два суббренда. Частное направление ESG Villas говорит о комфорте и качестве загородной жизни, коммерческое ESG Professional — о технологичности, сроках и ответственности подрядчика.",
        "Общая графическая база сохраняет узнаваемость компании: заказчик, пришедший из коммерческого проекта, понимает, что перед ним тот же подрядчик, что строит частные дома.",
      ],
      columns: [
        {
          title: "Материнский бренд ESG",
          items: [
            "Единый знак и логотипный блок",
            "Общая типографика",
            "Сдержанная тёмно-зелёная палитра",
            "Единые правила фотостиля",
          ],
        },
        {
          title: "ESG Villas — частное направление",
          items: [
            "Загородные дома под ключ",
            "Авторские проекты",
            "Акцент на визуализациях и материалах",
            "Тон: спокойный, доверительный",
          ],
        },
        {
          title: "ESG Professional — коммерческое направление",
          items: [
            "Гостиницы и общественные объекты",
            "Банные и термальные комплексы",
            "Акцент на компетенциях и сроках",
            "Тон: деловой, фактологичный",
          ],
        },
      ],
      images: [
        img(
          "esg-professional-villas-brand-system",
          "Логотипы двух направлений строительной компании: ESG Villas и ESG Professional",
          1600,
          1000,
          6,
        ),
      ],
      caption:
        "Два суббренда работают на разные аудитории, но остаются частью одной компании и одного производственного ресурса.",
    },
    {
      id: "structure",
      title: "Структура сайта: два входа в одну компанию",
      paragraphs: [
        "Сайт спроектировали так, чтобы посетитель с первого экрана попадал в свой сценарий. Частный заказчик уходит в каталог проектов домов и расчёт стоимости, представитель бизнеса — в раздел коммерческого строительства и форму тендера.",
      ],
      columns: [
        {
          title: "Уровень 1. Компания и доверие",
          items: ["Главная", "О компании", "Команда", "Реализованные объекты", "Контакты"],
        },
        {
          title: "Уровень 2. Частное направление",
          items: [
            "Каталог проектов домов",
            "Страница проекта с планировками",
            "Монолитные работы",
            "Дома из камня",
            "Дома из клееного бруса",
            "Отделочные работы",
          ],
        },
        {
          title: "Уровень 3. Коммерческое направление",
          items: [
            "ESG Professional",
            "Компетенции компании",
            "Гостиницы и общественные объекты",
            "Банные комплексы",
            "Портфолио коммерческих работ",
            "Участие в тендере",
          ],
        },
      ],
      images: [
        img(
          "esg-construction-website-homepage",
          "Первый экран сайта строительной компании ESG с разделением направлений",
          1600,
          1000,
          6,
        ),
      ],
    },
    {
      id: "catalog",
      title: "Каталог проектов домов: от подбора до расчёта стоимости",
      paragraphs: [
        "Частный заказчик редко приходит с готовым решением. Он сравнивает площадь, этажность, материал стен и бюджет. Каталог построили вокруг этих параметров: карточки одинаковы по структуре, поэтому проекты удобно сравнивать между собой.",
        "На странице проекта собраны визуализации, планировки этажей, состав работ и ориентировочная стоимость — набор данных, достаточный для первого разговора с менеджером.",
      ],
      columns: [
        {
          title: "Параметры подбора",
          items: ["Площадь дома", "Количество этажей", "Материал стен", "Бюджет строительства"],
        },
        {
          title: "Содержание карточки проекта",
          items: ["Название и площадь", "Визуализация фасада", "Основные характеристики", "Стоимость от"],
        },
        {
          title: "Что показываем на странице проекта",
          items: [
            "Галерея визуализаций",
            "Планировки этажей",
            "Технические характеристики",
            "Состав работ",
            "Форма расчёта стоимости",
          ],
        },
      ],
      images: [
        img("esg-house-projects-catalog", "Каталог проектов домов на сайте ESG", 1600, 1000),
        img("esg-house-projects-filters", "Фильтры подбора проекта дома по площади и материалу", 1600, 1000),
        img("esg-house-project-page", "Страница проекта загородного дома ESG", 1600, 1000),
        img("esg-house-project-plans", "Планировки этажей на странице проекта дома ESG", 1600, 1000),
      ],
      imageCols: 2,
    },
    {
      id: "b2b",
      title: "B2B-сценарий: компетенции вместо рекламных обещаний",
      paragraphs: [
        "Для коммерческого заказчика важна не эмоция, а подтверждение опыта. В разделе ESG Professional собрали виды работ, реализованные объекты и описание того, как компания ведёт проект — от проектной документации до сдачи.",
        "Отдельная форма позволяет пригласить компанию к участию в тендере: заказчик указывает тип объекта, площадь и адрес, а не заполняет универсальную форму «оставьте заявку».",
      ],
      columns: [
        {
          title: "Что показываем бизнесу",
          items: [
            "Компетенции и виды работ",
            "Реализованные коммерческие объекты",
            "Порядок работы над проектом",
            "Контакты профильных специалистов",
          ],
        },
        {
          title: "Типы объектов",
          items: [
            "Гостиницы и апарт-отели",
            "Банные и термальные комплексы",
            "Общественные здания",
            "Производственные и складские объекты",
          ],
        },
        {
          title: "Сценарии обращения",
          items: [
            "Пригласить в тендер",
            "Запросить коммерческое предложение",
            "Обсудить объект с инженером",
            "Получить консультацию по срокам",
          ],
        },
      ],
      images: [
        img("esg-professional-competence", "Компетенции строительной компании ESG Professional", 1600, 1000),
        img("esg-commercial-object-page", "Страница коммерческого объекта ESG Professional", 1600, 1000),
        img("esg-hotel-construction-page", "Страница строительства гостиниц ESG Professional", 1600, 1000),
        img("esg-professional-expertise", "Опыт коммерческого строительства ESG Professional", 1600, 1000),
      ],
      imageCols: 2,
    },
    {
      id: "seo",
      title: "SEO-архитектура: посадочные страницы под виды работ и материалы",
      paragraphs: [
        "Спрос в строительстве распадается на десятки узких запросов: монолитные работы, дома из камня, дома из клееного бруса, отделочные работы, строительство бань, гостиниц. Одной страницей услуг такой спрос не собрать.",
        "Мы разложили семантику на кластеры и под каждый сделали самостоятельную страницу с описанием технологии, этапов, реализованных объектов и собственной формой. Это даёт поиску понятную структуру, а пользователю — релевантный ответ на его запрос.",
      ],
      columns: [
        {
          title: "Кластер «Виды работ»",
          items: ["Монолитные работы", "Каменные работы", "Отделочные работы", "Инженерные системы"],
        },
        {
          title: "Кластер «Материалы»",
          items: ["Дома из камня", "Дома из клееного бруса", "Комбинированные дома"],
        },
        {
          title: "Кластер «Типы объектов»",
          items: ["Загородные дома", "Бани и банные комплексы", "Гостиницы", "Общественные здания"],
        },
      ],
      images: [
        img("esg-service-monolithic-page", "Страница услуги монолитных работ ESG", 1600, 1000),
        img("esg-service-glulam-page", "Страница строительства домов из клееного бруса ESG", 1600, 1000),
      ],
      imageCols: 2,
      caption:
        "Каждая страница закрывает свой кластер запросов и ведёт к собственному сценарию обращения.",
    },
    {
      id: "ads",
      title: "Яндекс Директ и аналитика: раздельные сценарии для двух направлений",
      paragraphs: [
        "Рекламу разделили по тому же принципу, что и сайт. Кампании частного направления ведут на каталог проектов и расчёт стоимости, коммерческого — на страницы компетенций и форму тендера. Общая посадочная страница для обоих типов спроса заменена на профильные.",
        "Аналитика настроена на конкретные действия: отправка формы расчёта, заявка со страницы проекта, обращение из B2B-раздела и звонок. Это позволяет оценивать не общий трафик, а стоимость обращения по каждому направлению.",
      ],
      columns: [
        {
          title: "Кампании частного направления",
          items: [
            "Строительство домов под ключ",
            "Дома по материалу стен",
            "Проекты домов по площади",
            "Ретаргетинг на просмотр проектов",
          ],
        },
        {
          title: "Кампании коммерческого направления",
          items: [
            "Строительство гостиниц",
            "Банные и термальные комплексы",
            "Общественные объекты",
            "Подрядчик для тендера",
          ],
        },
        {
          title: "Что считаем в аналитике",
          items: [
            "Заявки из формы расчёта стоимости",
            "Обращения со страниц проектов",
            "Заявки из B2B-раздела и тендерной формы",
            "Звонки и переходы в мессенджеры",
          ],
        },
      ],
      images: [
        img("esg-tender-form", "Форма участия в тендере на сайте ESG Professional", 1600, 1000),
        img("esg-contacts", "Страница контактов строительной компании ESG", 1600, 1000),
      ],
      imageCols: 2,
    },
  ],

  story: [],
  features: [],

  design: {
    title: "Визуальный язык: спокойный фон для сложного продукта",
    paragraphs: [
      "Строительство — долгая и дорогая покупка, поэтому интерфейс не давит на пользователя рекламными приёмами. Основную работу делают фотографии объектов, визуализации и структурированные данные.",
      "Тёмно-зелёная палитра и крупная типографика удерживают ощущение солидности, а одинаковая сетка карточек делает сравнение проектов быстрым.",
    ],
    bullets: [
      "Первый экран разделён на два входа: подбор проекта дома и коммерческое строительство.",
      "Карточки проектов построены на единой сетке — площадь, этажность и стоимость всегда на одном месте.",
      "Страницы услуг собраны по одному шаблону: технология, этапы, объекты, форма.",
      "Формы короткие: имя, телефон и параметр объекта — без лишних обязательных полей.",
    ],
  },

  identity: {
    fonts: [],
    colors: [
      { hex: "#1F2A1C", name: "Тёмно-зелёный, основной" },
      { hex: "#7A9A3F", name: "Акцентный зелёный" },
      { hex: "#F2F2F0", name: "Светлый фон" },
      { hex: "#0D0F0E", name: "Графитовый текст" },
    ],
  },

  cover: img(
    "esg-construction-company-case-cover",
    "Сайт строительной компании ESG на широком экране",
    1600,
    1000,
    6,
  ),
  gallery: [
    img("esg-about-company", "Страница «О компании» строительной компании ESG", 1600, 1000),
    img("esg-team", "Команда строительной компании ESG", 1600, 1000),
    img("esg-professional-works", "Портфолио коммерческих объектов ESG Professional", 1600, 1000),
    img("esg-commercial-construction-website", "Раздел коммерческого строительства ESG", 1600, 1000),
  ],

  responsive: {
    text: [
      "Значительная часть обращений в строительстве приходит с телефона: заказчик смотрит проекты вечером, сравнивает планировки и оставляет заявку прямо со страницы объекта.",
      "На мобильных каталог перестраивается в одну колонку, фильтры выносятся в отдельный экран выбора, а планировки открываются с возможностью увеличения.",
      "Формы расчёта стоимости и тендера адаптированы под ввод с телефона: короткие поля, крупные кнопки и телефонная клавиатура для контакта.",
    ],
    images: [
      img(
        "esg-construction-website-mobile-home",
        "Мобильная версия сайта ESG: главная страница",
        585,
        1266,
        2,
        false,
      ),
      img(
        "esg-construction-website-mobile-catalog",
        "Мобильная версия сайта ESG: каталог проектов домов",
        585,
        1266,
        2,
        false,
      ),
      img(
        "esg-construction-website-mobile-project",
        "Мобильная версия сайта ESG: страница проекта дома",
        585,
        1266,
        2,
        false,
      ),
      img(
        "esg-construction-website-mobile-pro",
        "Мобильная версия сайта ESG: раздел коммерческого строительства",
        585,
        1266,
        2,
        false,
      ),
      img(
        "esg-construction-website-mobile-service",
        "Мобильная версия сайта ESG: страница услуги",
        585,
        1266,
        2,
        false,
      ),
      img(
        "esg-construction-website-mobile-tender",
        "Мобильная версия сайта ESG: форма участия в тендере",
        585,
        1266,
        2,
        false,
      ),
    ],
  },

  tech: {
    title: "Технологическая основа проекта",
    paragraphs: [
      "Сайт собран на Tilda с кастомным кодом: это позволило быстро запустить структуру, сохранить управляемость контентом на стороне клиента и наращивать посадочные страницы под новые кластеры запросов.",
    ],
    cards: [
      { title: "Tilda с кастомным кодом", text: "Платформа сайта и управление контентом на стороне клиента." },
      { title: "Каталог проектов домов", text: "Карточки объектов, планировки и характеристики в единой сетке." },
      { title: "Подбор по параметрам", text: "Площадь, этажность, материал стен и бюджет строительства." },
      { title: "Посадочные страницы услуг", text: "Отдельные страницы под виды работ, материалы и типы объектов." },
      { title: "Формы обращения", text: "Расчёт стоимости, заявка с проекта и приглашение в тендер." },
      { title: "Адаптивная версия", text: "Каталог, планировки и формы работают на смартфонах и планшетах." },
      { title: "Веб-аналитика", text: "Настройка целей на формы, звонки и переходы в мессенджеры." },
      { title: "Яндекс Директ", text: "Раздельные кампании под частный и коммерческий спрос." },
    ],
  },

  scale: {
    title: "Масштаб реализованной системы",
    paragraphs: [
      "Проект работает как единая система: бренд, сайт, поисковая структура и реклама развиваются в одной логике и расширяются без пересборки.",
    ],
    metrics: [
      { value: "2", label: "направления бренда: ESG Villas и ESG Professional" },
      { value: "4", label: "параметра подбора проекта дома" },
      { value: "4", label: "кластера SEO-страниц: работы, материалы, объекты, направления" },
      { value: "3", label: "сценария обращения: расчёт, заявка с проекта, тендер" },
    ],
    note: "Данные отражают структуру реализованной системы, а не рекламные показатели клиента.",
  },

  servicesDone: {
    title: "Что мы сделали для ESG",
    items: [
      {
        label: "Фирменный стиль и архитектура бренда",
        to: routes.corporate,
        note: "Единый знак ESG и два суббренда под разные аудитории",
      },
      {
        label: "Аналитика и проектирование",
        to: routes.corporate,
        note: "Сценарии частного и коммерческого заказчика, структура разделов",
      },
      {
        label: "UX/UI-дизайн",
        to: routes.corporate,
        note: "Визуальная система каталога, страниц проектов и B2B-раздела",
      },
      {
        label: "Разработка сайта",
        to: routes.corporate,
        note: "Основная услуга проекта",
      },
      {
        label: "SEO-архитектура и продвижение",
        to: routes.seo,
        note: "Кластеры страниц под виды работ, материалы и типы объектов",
      },
      {
        label: "Яндекс Директ",
        to: routes.context,
        note: "Раздельные кампании под частное и коммерческое направления",
      },
      {
        label: "Веб-аналитика",
        to: routes.seo,
        note: "Цели на формы, звонки и обращения из B2B-раздела",
      },
      {
        label: "Поддержка и развитие проекта",
        to: routes.corporate,
        note: "Новые посадочные страницы и обновление контента",
      },
    ],
  },
};
